// Account safety: reporting, blocking and deleting an account.
//
// Server side lives in cloudloop-flask (docs/ACCOUNT_DELETION_AND_SAFETY.md):
//   POST   /api/reports                  {target_type, target_id, reason, details?}
//   POST   /api/users/<username>/block   DELETE the same path to unblock
//   GET    /api/blocks                   -> {blocked: [usernames]}
//   POST   /api/user/delete              {password}
//
// Pure functions only, so they can be tested without a browser.

export const REPORT_TARGET_TYPES = ["session", "loop", "user"];

// Order is the order the dialog lists them in. Values match the server's
// app/reports.py REASONS exactly.
export const REPORT_REASONS = [
  { value: "spam", label: "Spam or scam" },
  { value: "harassment", label: "Harassment or hate" },
  { value: "explicit", label: "Sexual or violent content" },
  { value: "copyright", label: "Uses my work without permission" },
  { value: "impersonation", label: "Pretending to be someone else" },
  { value: "other", label: "Something else" },
];

export const REPORT_DETAILS_MAX = 2000;

// The request body for POST /api/reports, or throws an Error naming what is
// wrong (the dialog never sends a body the server would refuse with 400).
export function buildReportPayload({ targetType, targetId, reason, details } = {}) {
  if (!REPORT_TARGET_TYPES.includes(targetType)) {
    throw new Error(`target type must be one of ${REPORT_TARGET_TYPES.join(", ")}`);
  }
  const id = typeof targetId === "string" ? targetId.trim() : "";
  if (!id) throw new Error("nothing to report");
  if (!REPORT_REASONS.some((r) => r.value === reason)) {
    throw new Error("choose a reason");
  }
  const text = typeof details === "string" ? details.trim() : "";
  if (text.length > REPORT_DETAILS_MAX) {
    throw new Error(`details must be at most ${REPORT_DETAILS_MAX} characters`);
  }
  const body = { target_type: targetType, target_id: id, reason };
  if (text) body.details = text;
  return body;
}

// What the person sees when a report could not be sent.
export function reportErrorMessage(status, targetType) {
  switch (status) {
    case 404:
      return targetType === "user"
        ? "That account no longer exists."
        : "That session no longer exists.";
    case 429:
      return "You've sent a lot of reports in the last hour. Please try again later.";
    case 401:
      return "Sign in again to send a report.";
    default:
      return "Could not send your report. Try again.";
  }
}

// Body for POST /api/register_user. accept_terms is sent only when the form
// that submitted it showed the 13+ / Terms / Privacy statement beside its
// button (the shared TermsConsent component), so pressing that button is the
// acceptance. A form that does not show it must not pass acceptTerms: true,
// or the server would store an acceptance nobody saw.
export function registerPayload(
  username,
  email,
  password,
  referredBy = null,
  acceptTerms = false,
) {
  return {
    username,
    email,
    password,
    ...(acceptTerms === true ? { accept_terms: true } : {}),
    ...(referredBy ? { referred_by: referredBy } : {}),
  };
}

// The method, path (under /api) and body for each account-safety call, kept
// here so tests can check them against the server's routes.
export function safetyRequestFor(action, arg) {
  switch (action) {
    case "block":
      return { method: "POST", path: `/users/${encodeURIComponent(arg)}/block` };
    case "unblock":
      return { method: "DELETE", path: `/users/${encodeURIComponent(arg)}/block` };
    case "blocks":
      return { method: "GET", path: "/blocks" };
    case "report":
      return { method: "POST", path: "/reports", body: arg };
    case "deleteAccount":
      return { method: "POST", path: "/user/delete", body: { password: arg } };
    default:
      throw new Error(`unknown safety action: ${action}`);
  }
}

// What the person sees when account deletion fails. 503 carries the server's
// own message (the deletion is safe to retry, and the message says so).
export function deleteAccountErrorMessage(status, serverMessage) {
  switch (status) {
    case 403:
      return "That password is not right.";
    case 400:
      return "Enter your password.";
    case 401:
      return "Your sign-in has expired. Sign in again, then delete your account.";
    case 503:
      return serverMessage || "We could not finish deleting your account. Please try again.";
    default:
      return "Could not delete your account. Try again.";
  }
}

// Plain-words summary of what deletion does, shown on /delete-account (the
// Google Play deletion URL) and /settings/account. Keep in step with the
// server's policy in cloudloop-flask docs/ACCOUNT_DELETION_AND_SAFETY.md.
export const DELETION_REMOVES = [
  "Your recordings, everywhere, including loops you added to sessions with other people.",
  "Sessions you started that nobody else joined.",
  "Packs you made that nobody has bought.",
  "Your profile, picture, bio, friends, favorites and starred loops.",
  "Invitations you sent or received, people you blocked, notification devices and payout settings.",
  "Your sign-in. Every device signed in to the account is signed out.",
];

export const DELETION_KEEPS = [
  "Loops and packs someone has already bought stay with the buyer. They no longer show your name; they show as deleted-user.",
  "Sessions other people are in stay for them. The earliest other member becomes the owner, and your loops are taken out.",
  "Records of purchases and sales, which we keep as financial records.",
  "Reports you filed, without your name on them.",
  "Your username is retired: nobody can sign up with it again.",
];

// Normalises GET /api/blocks results to a sorted list of usernames.
export function blockedUsernames(results) {
  const list = results && Array.isArray(results.blocked) ? results.blocked : [];
  return list
    .map((u) => (typeof u === "string" ? u : u && u.username))
    .filter((u) => typeof u === "string" && u)
    .sort((a, b) => a.localeCompare(b));
}
